import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MembershipData } from "./types";
import { useTheme } from "../../../context/ThemeContext";
import { useParticipantLabel } from "../../../hooks/useParticipantLabel";
import { captureError } from "../../../utils/sentry";
import { getSettings } from "../../../api/SettingsAPI";
import { isMemberRole } from "../../../utils/roles";
import { CONFIRMATION_DEFAULTS } from "./confirmationDefaults";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getLightBorder,
  getButtonStyles,
} from "../../../utils/themeUtils";
import { useViewportFlags } from "../../../hooks/useViewportFlags";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { CommunicationChoicesSummary } from "../../registration/CommunicationChoices";
import { safeGetItem } from "@mediatechgroup/app-version";

interface Props {
  formData?: MembershipData;
  selectedPrograms?: string[];
  paymentSuccess?: boolean;
}

const MembershipOverview: React.FC<Props> = () => {
  const location = useLocation();
  const {
    formData,
    selectedPrograms,
    paymentSuccess,
    paymentPending,
    communicationChoices,
    waiverDeclines,
    secondParent,
  } = location.state || {};
  const declinedWaivers: any[] = Array.isArray(waiverDeclines?.declined) ? waiverDeclines.declined : [];
  const [activeAthletes, setActiveAthletes] = useState<number[]>([]);
  const { isMobileDevice, isMobileLandscape } = useViewportFlags();
  // SETTINGS
  const [settings, setSettings] = useState<any>(null);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const participant = useParticipantLabel();
  getButtonStyles(isDarkMode);
  // HELPER
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const xTenantId = pathParts[0] || null;
  const role = safeGetItem("userRole");

  const toggleAthleteDetails = (index: number) => {
    setActiveAthletes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleBackButtonClick = () => {
    // Check if payment method is "cash"
    if (role && !isMemberRole(role)) {
      navigate(`/${xTenantId}/admin/add-member`); // Navigate to the add-member page if payment method is cash
    } else {
      navigate(`/${xTenantId}/login`); // Otherwise, navigate to login page
    }
  };


  // Fetch settings from API (public endpoint, no auth required)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        if (response?.success && response.data) {
          setSettings(response.data);
        }
      } catch {
        // Fallback to localStorage
        const savedSettings = safeGetItem('settings');
        if (savedSettings) {
          try {
            setSettings(JSON.parse(savedSettings));
          } catch (error) {
            captureError(error, { component: 'MembershipOverview', operation: 'parseSettings' });
          }
        }
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className={`${getPageBackground(isDarkMode)} ${
      isMobileDevice
        ? 'p-0 min-h-screen'
        : 'py-16 px-8 sm:px-4'
    }`}>
      {/* Mobile Header */}
      {isMobileDevice && (
        <div className={`sticky top-0 ${getCardBackground(isDarkMode)} border-b ${getLightBorder(isDarkMode)} z-10 ${
          isMobileLandscape ? 'py-2 px-3' : 'py-4 px-4'
        }`}>
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackButtonClick}
              className={`flex items-center text-[var(--cc-link)] hover:opacity-80 transition-opacity ${
                isMobileLandscape ? 'text-sm' : 'text-base'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`mr-2 ${isMobileLandscape ? 'w-4 h-4' : 'w-5 h-5'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>
                {role && !isMemberRole(role) ? "Dashboard" : "Login"}
              </span>
            </button>
            
            {paymentSuccess && (
              <div className="flex items-center space-x-1 text-green-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className={isMobileLandscape ? 'text-xs' : 'text-sm'}>Success</span>
              </div>
            )}
          </div>
          
          <div className="mt-2">
            <h1 className={`font-bold text-gray-900 dark:text-white ${
              isMobileLandscape ? 'text-lg' : 'text-xl'
            }`}>
              Registration Summary
            </h1>
            <p className={`mt-2 ${getSecondaryText(isDarkMode)} ${
              isMobileLandscape ? 'text-xs' : 'text-sm'
            } leading-relaxed whitespace-pre-line`}>
              {settings?.confirmation_welcome_text || CONFIRMATION_DEFAULTS.welcome}
            </p>
          </div>
        </div>
      )}

      {/* Desktop Back Button */}
      {!isMobileDevice && (
        <button
          onClick={handleBackButtonClick}
          className="mb-4 flex items-center text-[var(--cc-link)] hover:opacity-80 transition-opacity"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span>
            {role && !isMemberRole(role) ? "Back To Dashboard" : "Back To Login"}
          </span>
        </button>
      )}

      {/* Container */}
      <div className={`${getCardBackground(isDarkMode)} w-full shadow-xl ${
        isMobileDevice
          ? 'min-h-screen'
          : 'max-w-6xl mx-auto rounded-lg p-8'
      } ${
        isMobileDevice
          ? isMobileLandscape ? 'p-3' : 'p-4'
          : ''
      }`}>
        {/* Desktop Header */}
        {!isMobileDevice && (
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              Registration Summary
            </h2>
            <p className={`text-base ${getSecondaryText(isDarkMode)} max-w-3xl mx-auto leading-relaxed whitespace-pre-line`}>
              {settings?.confirmation_welcome_text || CONFIRMATION_DEFAULTS.welcome}
            </p>
          </div>
        )}

        <div className={`${
          isMobileLandscape 
            ? 'grid grid-cols-3 gap-3' 
            : isMobileDevice 
              ? 'space-y-4' 
              : 'grid grid-cols-1 md:grid-cols-3 gap-8'
        }`}>
          {/* Membership Details */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6 space-y-4'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Member Info
              </h3>
            </div>
            
            <div className={`${
              isMobileDevice ? 'mt-4 space-y-3' : `text-sm ${getPrimaryText(isDarkMode)}`
            }`}>
              <div className={`${isMobileDevice ? `flex justify-between items-center py-2 border-b ${getLightBorder(isDarkMode)}` : ''}`}>
                <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  Name:
                </span>
                <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right` : ''} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  {formData?.firstName} {formData?.lastName}
                </span>
              </div>
              
              <div className={`${isMobileDevice ? `flex justify-between items-center py-2 border-b ${getLightBorder(isDarkMode)}` : ''}`}>
                <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  Email:
                </span>
                <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right` : ''} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  {formData?.email}
                </span>
              </div>

              <div className={`${isMobileDevice ? `flex justify-between items-center py-2 border-b ${getLightBorder(isDarkMode)}` : ''}`}>
                <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  Phone:
                </span>
                <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right` : ''} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  {formData?.phone}
                </span>
              </div>

              <div className={`${isMobileDevice ? `flex justify-between items-start py-2 border-b ${getLightBorder(isDarkMode)}` : ''}`}>
                <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  Address:
                </span>
                <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right max-w-[60%]` : ''} ${
                  isMobileLandscape ? 'text-xs' : 'text-sm'
                }`}>
                  {formData?.address}, {formData?.city}, {formData?.state} {formData?.zip}
                </span>
              </div>

              {formData && formData?.startDate && (
                <div className={`${isMobileDevice ? `flex justify-between items-center py-2 border-b ${getLightBorder(isDarkMode)}` : ''}`}>
                  <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                    isMobileLandscape ? 'text-xs' : 'text-sm'
                  }`}>
                    Start Date:
                  </span>
                  <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right` : ''} ${
                    isMobileLandscape ? 'text-xs' : 'text-sm'
                  }`}>
                    {formatDateOnly(formData?.startDate)}
                  </span>
                </div>
              )}

              {formData?.referral && (
                <div className={`${isMobileDevice ? 'flex justify-between items-center py-2' : ''}`}>
                  <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                    isMobileLandscape ? 'text-xs' : 'text-sm'
                  }`}>
                    Referral:
                  </span>
                  <span className={`${isMobileDevice ? `${getPrimaryText(isDarkMode)} text-right` : ''} ${
                    isMobileLandscape ? 'text-xs' : 'text-sm'
                  }`}>
                    {formData?.referral}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Athlete Details */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6 space-y-4'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {participant.plural} ({formData?.athletes?.length || 0})
              </h3>
            </div>
            
            <div className={`${isMobileDevice ? 'mt-4 space-y-3' : 'space-y-4'}`}>
              {formData?.athletes?.map((athlete: any, index: any) => (
                <div key={index} className={`${
                  isMobileDevice
                    ? `border ${getLightBorder(isDarkMode)} rounded-lg p-3`
                    : `text-sm ${getPrimaryText(isDarkMode)} space-y-2`
                }`}>
                  <button
                    onClick={() => toggleAthleteDetails(index)}
                    className={`w-full text-left font-semibold flex items-center justify-between text-gray-900 dark:text-white hover:opacity-80 transition-opacity ${
                      isMobileLandscape ? 'text-sm' : 'text-base'
                    }`}
                  >
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {athlete?.firstName} {athlete?.lastName}
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${
                        activeAthletes?.includes(index) ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {activeAthletes?.includes(index) && (
                    <div className={`${
                      isMobileDevice ? `mt-3 space-y-2 pt-3 border-t ${getLightBorder(isDarkMode)}` : 'ml-4 space-y-2'
                    }`}>
                      <div className={`${isMobileDevice ? 'grid grid-cols-2 gap-2' : ''}`}>
                        <div className={`${isMobileDevice ? 'flex justify-between items-center py-1' : ''}`}>
                          <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                            isMobileLandscape ? 'text-xs' : 'text-sm'
                          }`}>
                            Gender:
                          </span>
                          <span className={`${isMobileDevice ? getPrimaryText(isDarkMode) : ''} ${
                            isMobileLandscape ? 'text-xs' : 'text-sm'
                          }`}>
                            {athlete?.gender}
                          </span>
                        </div>

                        <div className={`${isMobileDevice ? 'flex justify-between items-center py-1' : ''}`}>
                          <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium` : 'font-bold'} ${
                            isMobileLandscape ? 'text-xs' : 'text-sm'
                          }`}>
                            Birth Date:
                          </span>
                          <span className={`${isMobileDevice ? getPrimaryText(isDarkMode) : ''} ${
                            isMobileLandscape ? 'text-xs' : 'text-sm'
                          }`}>
                            {formatDateOnly(athlete?.dateOfBirth, 'en-US', {
                              year: '2-digit',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>

                      {athlete?.programs?.length > 0 && (
                        <div>
                          <span className={`${isMobileDevice ? `${getSecondaryText(isDarkMode)} font-medium block mb-2` : 'font-bold'} ${
                            isMobileLandscape ? 'text-xs' : 'text-sm'
                          }`}>
                            Programs:
                          </span>
                          <div className={`${
                            isMobileDevice ? 'space-y-2' : 'space-y-1 ml-4'
                          }`}>
                            {athlete?.programs
                              ?.filter((program: any) => program?.programKey)
                              ?.map((program: any, idx: any) => (
                                <div key={idx} className={`${
                                  isMobileDevice
                                    ? `${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-2 rounded border ${getLightBorder(isDarkMode)}`
                                    : `text-sm ${getPrimaryText(isDarkMode)}`
                                }`}>
                                  <div className={`${isMobileDevice ? 'flex justify-between items-center' : ''}`}>
                                    <span className={`${
                                      isMobileDevice ? `font-medium ${getPrimaryText(isDarkMode)}` : 'font-bold'
                                    } ${isMobileLandscape ? 'text-xs' : 'text-sm'}`}>
                                      {program?.programKey}
                                    </span>
                                    {program?.slot && (
                                      <span className={`${
                                        isMobileDevice ? getSecondaryText(isDarkMode) : ''
                                      } ${isMobileLandscape ? 'text-xs' : 'text-sm'}`}>
                                        Slot: {program?.slot}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Program Selection */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6 space-y-4'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Selected Programs
              </h3>
            </div>
            
            <div className={`${isMobileDevice ? 'mt-4' : ''}`}>
              {selectedPrograms && selectedPrograms.length > 0 ? (
                <div className={`${
                  isMobileDevice ? 'space-y-2' : 'space-y-3'
                }`}>
                  {selectedPrograms.map((program: any, index: number) => {
                    const matchedPrograms = formData?.athletes?.filter(
                      (athlete: any) =>
                        athlete.programs?.some(
                          (p: any) => p.programKey === program
                        )
                    );

                    if (matchedPrograms.length > 0) {
                      return (
                        <div key={index} className={`${
                          isMobileDevice
                            ? `${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border ${getLightBorder(isDarkMode)} rounded-lg p-3`
                            : 'flex items-center space-x-2'
                        }`}>
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className={`font-medium ${getPrimaryText(isDarkMode)} ${
                              isMobileLandscape ? 'text-sm' : 'text-base'
                            }`}>
                              {program}
                            </span>
                          </div>
                          {isMobileDevice && (
                            <p className={`${getSecondaryText(isDarkMode)} mt-1 ${
                              isMobileLandscape ? 'text-xs' : 'text-sm'
                            }`}>
                              {matchedPrograms.length} athlete{matchedPrograms.length !== 1 ? 's' : ''} enrolled
                            </p>
                          )}
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              ) : (
                <div className={`text-center py-6 ${getSecondaryText(isDarkMode)}`}>
                  <svg className={`w-12 h-12 mx-auto ${getSecondaryText(isDarkMode)} mb-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className={isMobileLandscape ? 'text-sm' : 'text-base'}>
                    No programs selected
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customizable Information Sections */}
        <div className={`${
          isMobileLandscape
            ? 'grid grid-cols-3 gap-3 mt-3'
            : isMobileDevice
              ? 'space-y-4 mt-4'
              : 'grid grid-cols-1 md:grid-cols-3 gap-8 mt-8'
        }`}>
          {/* What to Expect */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                What to Expect
              </h3>
            </div>
            <p className={`${isMobileDevice ? 'mt-4' : 'mt-4'} ${getSecondaryText(isDarkMode)} ${
              isMobileLandscape ? 'text-xs' : 'text-sm'
            } leading-relaxed whitespace-pre-line`}>
              {settings?.confirmation_what_to_expect_text || CONFIRMATION_DEFAULTS.whatToExpect}
            </p>
          </div>

          {/* Schedule */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Schedule
              </h3>
            </div>
            <p className={`${isMobileDevice ? 'mt-4' : 'mt-4'} ${getSecondaryText(isDarkMode)} ${
              isMobileLandscape ? 'text-xs' : 'text-sm'
            } leading-relaxed whitespace-pre-line`}>
              {settings?.confirmation_schedule_text || CONFIRMATION_DEFAULTS.schedule}
            </p>
          </div>

          {/* Managing Your Account */}
          <div className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
            isMobileDevice ? 'p-4' : 'p-6'
          }`}>
            <div className={`${
              isMobileDevice
                ? `${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border-b ${getLightBorder(isDarkMode)} -mx-4 -mt-4 px-4 py-3 rounded-t-lg`
                : `border-b ${getLightBorder(isDarkMode)} pb-4`
            }`}>
              <h3 className={`font-semibold ${getPrimaryText(isDarkMode)} flex items-center ${
                isMobileDevice
                  ? isMobileLandscape ? 'text-base' : 'text-lg'
                  : 'text-2xl'
              }`}>
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Managing Your Account
              </h3>
            </div>
            <p className={`${isMobileDevice ? 'mt-4' : 'mt-4'} ${getSecondaryText(isDarkMode)} ${
              isMobileLandscape ? 'text-xs' : 'text-sm'
            } leading-relaxed whitespace-pre-line`}>
              {settings?.confirmation_manage_account_text || CONFIRMATION_DEFAULTS.manageAccount}
            </p>
          </div>
        </div>

        {/* What the family chose when they signed up: how we may contact them
            (as the server recorded it, with its date), any waiver they said
            no to, and anyone else they added. Only a public sign-up carries
            these; the staff add-member flows reach this page without them. */}
        {(communicationChoices || declinedWaivers.length > 0 || secondParent) && (
          <div
            className={`${getCardBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} shadow-sm ${
              isMobileDevice ? 'mt-4 p-4' : 'mt-8 p-6'
            }`}
            data-testid="registration-choices"
          >
            <CommunicationChoicesSummary choices={communicationChoices} />

            {declinedWaivers.length > 0 && (
              <div className="mt-4" data-testid="declined-waivers">
                <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>Your answers</h3>
                <ul className={`text-sm mt-2 space-y-1 ${getPrimaryText(isDarkMode)}`}>
                  {declinedWaivers.map((w: any) => (
                    <li key={w.waiver_id} className="flex justify-between gap-4">
                      <span>{w.name || "Waiver"}</span>
                      <span className="font-medium">No</span>
                    </li>
                  ))}
                </ul>
                {Number(waiverDeclines?.no_photos_athletes) > 0 && (
                  <p className={`text-xs mt-2 ${getSecondaryText(isDarkMode)}`}>
                    We have marked {participant.pluralLower} on this account "no photos" so our staff know.
                  </p>
                )}
              </div>
            )}

            {secondParent && (secondParent.first_name || secondParent.last_name) && (
              <div className="mt-4" data-testid="added-second-parent">
                <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>Also on this account</h3>
                <p className={`text-sm mt-1 ${getPrimaryText(isDarkMode)}`}>
                  {secondParent.first_name} {secondParent.last_name}
                </p>
                {secondParent.receive_billing && (
                  <p className={`text-xs mt-1 ${getSecondaryText(isDarkMode)}`}>Billing emails will go to them too.</p>
                )}
                {secondParent.wants_login && (
                  <p className={`text-xs mt-1 ${getSecondaryText(isDarkMode)}`}>
                    We are emailing them a link to set up their own login.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* The account was created but the card could not be charged. */}
        {paymentPending && !paymentSuccess && (
          <div
            className={`rounded-lg border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} ${
              isMobileDevice ? 'mt-4 p-4' : 'mt-8 p-6'
            }`}
            data-testid="registration-payment-pending"
          >
            <h4 className={`font-bold ${getPrimaryText(isDarkMode)}`}>Your account is set up</h4>
            <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
              We could not process your card just now. The club will follow up about payment, and you
              can also pay from your account after you sign in.
            </p>
          </div>
        )}

        {/* Payment Success Message */}
        {paymentSuccess && (
          <div className={`bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-lg ${
            isMobileDevice 
              ? isMobileLandscape ? 'mt-4 p-3' : 'mt-6 p-4'
              : 'mt-8 p-6'
          }`}>
            <div className="flex items-center space-x-3">
              <svg className={`${
                isMobileLandscape ? 'w-6 h-6' : 'w-8 h-8'
              } text-white`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className={`font-bold ${
                  isMobileLandscape ? 'text-base' : 'text-lg'
                }`}>
                  Registration Complete!
                </h4>
                <p className={`${
                  isMobileLandscape ? 'text-sm' : 'text-base'
                } opacity-90`}>
                  Payment was successful and your registration is now complete.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipOverview;
